import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ArrowUp } from "lucide-react";
import { t } from "@/i18n";
import { DictateButton } from "@/components/DictateButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sessionId = (slug) => {
  const key = `smboi_customer_session${slug ? `_${slug}` : ""}`;
  let id = localStorage.getItem(key);
  if (!id) { id = crypto.randomUUID(); localStorage.setItem(key, id); }
  return id;
};

export default function CustomerChat() {
  const { slug = "" } = useParams();
  const cfg = { params: slug ? { org: slug } : {} };
  const [info, setInfo] = useState(null);
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const sid = useRef(sessionId(slug)).current;
  const end = useRef(null);
  useEffect(() => {
    const c = { params: slug ? { org: slug } : {} };
    axios.get(`${API}/public/info`, c).then((r) => setInfo(r.data)).catch((e) => setErr(t(e?.response?.status === 404 ? "Business not found. Check the link and try again." : "Chat is unavailable right now.")));
    const load = () => axios.get(`${API}/public/session/${sid}`, c).then((r) => setItems(r.data)).catch(() => {});
    load();
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [sid, slug]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [items.length]);
  const send = async (e) => {
    e.preventDefault();
    const q = text.trim();
    if (!q) return;
    setBusy(true); setErr(""); setText("");
    try {
      const { data } = await axios.post(`${API}/public/ask`, { session_id: sid, question: q }, cfg);
      setItems((p) => [...p, data]);
    } catch (e2) { setErr(e2?.response?.data?.detail || t("Something went wrong")); setText(q); }
    setBusy(false);
  };
  return (
    <div className="min-h-screen flex flex-col max-w-xl mx-auto px-4" data-testid="customer-chat">
      <header className="py-5 flex items-center gap-3">
        <img src="/mascot.png" alt="" className="h-10 w-auto" />
        {info?.logo_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${info.logo_url}`} alt="" className="h-10 w-10 rounded-lg object-contain bg-white" data-testid="customer-org-logo" />}
        <div>
          <p className="font-bold text-lg leading-tight" data-testid="customer-business-name">{info?.business_name || "…"}</p>
          <p className="text-xs faint">{t("Answers come from what the team has confirmed.")}</p>
        </div>
      </header>
      <main className="flex-1 stack-sm pb-4">
        {info && <div className="bubble-in self-start max-w-[85%] rounded-2xl px-4 py-2 bg-[var(--bg-surface)]" data-testid="customer-greeting">{info.greeting}</div>}
        {items.map((q) => (
          <div key={q.id} className="stack-sm" data-testid={`customer-item-${q.id}`}>
            <p className="self-end max-w-[85%] rounded-2xl px-4 py-2 bg-[var(--accent-primary)] text-white">{q.question}</p>
            <div className="self-start max-w-[85%] rounded-2xl px-4 py-2 bg-[var(--bg-surface)]" data-testid={`customer-answer-${q.id}`}>
              {q.answer}
              {!!q.sources?.length && <p className="text-xs faint mt-1">{t("From")}: {q.sources.join(", ")}</p>}
            </div>
            {q.owner_reply && (
              <div className="self-start max-w-[85%] rounded-2xl px-4 py-2 bg-[var(--ok-bg,var(--bg-surface))] border border-[var(--status-ok)]" data-testid={`customer-owner-reply-${q.id}`}>
                <p className="text-xs font-semibold text-[var(--status-ok)]">{t("Reply from the team")}</p>{q.owner_reply}
              </div>
            )}
          </div>
        ))}
        <div ref={end} />
      </main>
      <div className="sticky bottom-0 pb-4 pt-2 bg-[var(--bg-primary)] stack-sm">
        {err && <p className="text-sm text-[var(--status-error)]" data-testid="customer-error">{err}</p>}
        {info?.web_enabled !== false && (
          <form onSubmit={send} className="flex gap-2">
            <DictateButton post={(fd) => axios.post(`${API}/public/transcribe`, fd, cfg)} onText={(s) => setText((p) => (p ? `${p} ${s}` : s))} testid="customer-dictate-button" />
            <input className="field" value={text} onChange={(e) => setText(e.target.value)} placeholder={t("Ask about hours, menu, allergens…")} data-testid="customer-input" />
            <button className="btn btn-primary !px-3" disabled={busy || !text.trim()} data-testid="customer-send" aria-label={t("Send")}><ArrowUp size={18} /></button>
          </form>
        )}
      </div>
    </div>
  );
}
